import {
  Button,
  Col,
  DatePicker,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Tooltip,
} from "antd";
import {
  CloseOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  FileAddOutlined,
  PlusOutlined,
  SettingOutlined,
  TableOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { BiFilterAlt } from "react-icons/bi";
import dayjs from "dayjs";
import Papa from "papaparse";
import { Country } from "country-state-city";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Client } from "../../core/api-client";
import SalesBulkUpload from "./components/sales-bulk-upload.component";
import {
  createSales,
  deleteSalesById,
  getSalesList,
  updateSalesById,
} from "../../core/store/reducers/sales.slice";
import { requestGetByModuleCommon } from "../../core/store/reducers/master.slice";
import DataTableComponent from "../../shared/components/data-table/data-table.component";
import { API_PREFIX_V1, TABLE_ROWS_COUNT } from "../../shared/constants";
import { allPermissions } from "../../shared/constants/constants";
import {
  API_GET_ALL_ACCOUNTS_LIST,
  API_GET_ALL_STAFF_LIST,
  API_GET_DEAL_STAGE_ACTIVE,
  API_GET_DEAL_SUB_STAGE_BY_STAGE,
  API_LOOKUP_FOREX_RATE,
  ROUTE_MANAGE_FOREX,
} from "../../shared/routes/private.routes";
import { RootState } from "../../shared/types/common";
import {
  accessAllowed,
  formatCost1,
  showNotification,
} from "../../shared/utils/utils";

const SALES_PAGE_SIZE = 15;

const formatClosureQuarter = (value: string) => {
  if (!value) return "";
  const fiscal = /^(\d{4})-(\d{2}|\d{4})-Q([1-4])$/.exec(value);
  if (fiscal) return `FY${fiscal[2].slice(-2)}-Q${fiscal[3]}`;
  const calendar = /^(\d{4})-Q([1-4])$/.exec(value);
  return calendar ? `FY${calendar[1].slice(-2)}-Q${calendar[2]}` : value;
};

const compareOptionLabels = (a: { label?: string }, b: { label?: string }) =>
  String(a.label || "").localeCompare(String(b.label || ""), "en", { sensitivity: "base" });

const DEFAULT_COLUMNS = [
  "dealReference",
  "entityName",
  "clientName",
  "opportunity",
  "dealStageName",
  "dealSubStageName",
  "dealValue",
  "dealValueTarget",
  "probability",
  "priority",
  "estimatedClosureDate",
];

const ManageSalesPage: React.FC = () => {
  const pageRef = useRef<HTMLDivElement>(null);
  const [contentLeft, setContentLeft] = useState(0);
  useLayoutEffect(() => {
    const content = pageRef.current?.closest("main") || pageRef.current;
    if (!content) return;
    const updateBounds = () => setContentLeft(Math.max(0, content.getBoundingClientRect().left));
    updateBounds();
    const observer = new ResizeObserver(updateBounds);
    observer.observe(content);
    window.addEventListener("resize", updateBounds);
    return () => { observer.disconnect(); window.removeEventListener("resize", updateBounds); };
  }, []);
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [filterForm] = Form.useForm();
  const { loadingSales, salesList, pagination } = useSelector(
    (state: RootState) => state.sales
  );
  const { myPermissions } = useSelector((state: RootState) => state.auth);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [generatingQuotation, setGeneratingQuotation] = useState(false);
  const quotationRequest = useRef(0);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [filterVisible, setFilterVisible] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState<any>(null);
  useEffect(() => {
    quotationRequest.current++;
    setGeneratingQuotation(false);
  }, [drawerOpen, selectedEntity]);
  const [pageSize, setPageSize] = useState(SALES_PAGE_SIZE);
  const [filters, setFilters] = useState<any>({});
  const [entityList, setEntityList] = useState<any[]>([]);
  const closureDate = Form.useWatch("estimatedClosureDate", form);
  const closureEntityId = Form.useWatch("entityId", form);
  const closureEntity = entityList.find((entity) => entity.id === closureEntityId);
  const closureQuarter = (() => {
    if (!closureDate || !closureEntity) return "";
    const date = dayjs(closureDate);
    if (!date.isValid()) return "";
    const month = date.month();
    const year = date.year();
    if (closureEntity.code === "RDIN") {
      const startYear = month < 3 ? year - 1 : year;
      return `FY${String((startYear + 1) % 100).padStart(2, "0")}-Q${Math.floor(((month + 9) % 12) / 3) + 1}`;
    }
    return `FY${String(year % 100).padStart(2, "0")}-Q${Math.floor(month / 3) + 1}`;
  })();
  const [projectTypes, setProjectTypes] = useState<any[]>([]);
  const [currencies, setCurrencies] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [staff, setStaff] = useState<any[]>([]);
  const [stages, setStages] = useState<any[]>([]);
  const [subStages, setSubStages] = useState<any[]>([]);
  const [converted, setConverted] = useState<any>(null);
  const [visibleColumns, setVisibleColumns] = useState<string[]>(DEFAULT_COLUMNS);
  const [columnSettingsOpen, setColumnSettingsOpen] = useState(false);

  const loadList = useCallback(
    (page: any = null) => {
      dispatch(
        getSalesList({
          params: { page: page || pagination?.page || 1, limit: pageSize },
          body: filters,
        })
      );
    },
    [dispatch, filters, pageSize, pagination?.page]
  );

  useEffect(() => {
    loadList(1);
  }, [filters, pageSize]);

  useEffect(() => {
    fetchReferenceData();
  }, []);

  const fetchReferenceData = async () => {
    const [entities, types, currencyRes, accountRes, staffRes, stageRes] =
      await Promise.all([
        dispatch(requestGetByModuleCommon({ module: "master_entity" })).unwrap(),
        dispatch(requestGetByModuleCommon({ module: "master_project_type" })).unwrap(),
        dispatch(requestGetByModuleCommon({ module: "master_currency" })).unwrap(),
        Client.get(API_GET_ALL_ACCOUNTS_LIST),
        Client.get(API_GET_ALL_STAFF_LIST),
        Client.get(API_GET_DEAL_STAGE_ACTIVE),
      ]);
    setEntityList(entities?.data?.items || []);
    setProjectTypes(types?.data?.items || []);
    setCurrencies(currencyRes?.data?.items || []);
    setAccounts(accountRes?.data?.data || []);
    setStaff(staffRes?.data?.data || []);
    setStages(stageRes?.data?.data || []);
  };

  const currencyOptions = currencies.map((currency) => ({
    value: currency.id,
    label: currency.name,
  })).sort(compareOptionLabels);

  const staffOptions = staff.map((item) => ({
    value: item.staffId,
    label: item.employeeId ? `${item.staffName} (${item.employeeId})` : item.staffName,
  })).sort((a, b) =>
    String(a.label || "").localeCompare(String(b.label || ""), undefined, { sensitivity: "base" })
  );

  const loadSubStages = async (dealStageId: string) => {
    if (!dealStageId) {
      setSubStages([]);
      return;
    }
    const response = await Client.get(API_GET_DEAL_SUB_STAGE_BY_STAGE + dealStageId);
    setSubStages(response?.data?.data || []);
  };

  const lookupConversion = async () => {
    const values = form.getFieldsValue();
    if (!values.dealCurrencyId || !values.targetCurrencyId || !values.dealValue) {
      setConverted(null);
      return;
    }
    const month = dayjs().format("YYYY-MM");
    const response = await Client.get(API_LOOKUP_FOREX_RATE, {
      params: {
        from: values.dealCurrencyId,
        to: values.targetCurrencyId,
        month,
      },
    });
    if (response?.data?.success) {
      const rate = Number(response.data.data.rate);
      setConverted({
        rate,
        value: Number(values.dealValue) * rate,
        month: response.data.data.month,
      });
      return;
    }
    setConverted(null);
    const fromName = currencies.find((c) => c.id === values.dealCurrencyId)?.name;
    const toName = currencies.find((c) => c.id === values.targetCurrencyId)?.name;
    Modal.confirm({
      title: "Conversion rate missing",
      content: `No conversion rate is configured for ${fromName} -> ${toName} for ${dayjs().format("MMM-YY")}. Do you want to configure it now?`,
      okText: accessAllowed(myPermissions, allPermissions.CREATE_FOREX)
        ? "Configure"
        : "OK",
      cancelText: "Cancel",
      onOk: () => {
        if (accessAllowed(myPermissions, allPermissions.CREATE_FOREX)) {
          window.location.href = `${ROUTE_MANAGE_FOREX}?from=${values.dealCurrencyId}&to=${values.targetCurrencyId}&month=${month}`;
        }
      },
    });
  };

  const openCreate = () => {
    setSelectedEntity(null);
    setConverted(null);
    form.resetFields();
    form.setFieldsValue({ targetCurrencyId: currencies.find((c) => c.name === "MYR")?.id, priority: "2" });
    setDrawerOpen(true);
  };

  const openEdit = async (record: any) => {
    setSelectedEntity(record);
    await loadSubStages(record.dealStageId);
    setConverted({
      rate: Number(record.conversionRateUsed),
      value: Number(record.dealValueTarget),
      month: record.conversionRateMonth,
    });
    form.setFieldsValue({
      ...record,
      dealValue: Number(record.dealValue),
      grossMargin:
        record.grossMargin === null || record.grossMargin === undefined
          ? undefined
          : Number(record.grossMargin),
      probability:
        record.probability === null || record.probability === undefined
          ? undefined
          : Number(record.probability),
      receivedDate: record.receivedDate ? dayjs(record.receivedDate) : null,
      estimatedClosureDate: record.estimatedClosureDate
        ? dayjs(record.estimatedClosureDate)
        : null,
    });
    setDrawerOpen(true);
  };

  const deleteSales = (record: any) => {
    Modal.confirm({
      title: "Delete sales record",
      content: "Are you sure you want to delete this sales record?",
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        const result = await dispatch(deleteSalesById(record.salesId));
        if (result?.payload?.data?.statusCode === 200) {
          showNotification("Sales record deleted successfully", "success");
          loadList();
        } else {
          showNotification("Unable to delete sales record", "error");
        }
      },
    });
  };

  const generateQuotationNumber = async () => {
    if (generatingQuotation) return;
    const request = ++quotationRequest.current;
    setGeneratingQuotation(true);
    try {
      const response = await Client.post(`${API_PREFIX_V1}sales/quotation-number`, {}, { shouldHide: true });
      if (request !== quotationRequest.current) return;
      if (response?.status >= 200 && response.status < 300 && response.data?.data?.quotationNumber) {
        form.setFieldValue("quotationSowNumber", response.data.data.quotationNumber);
      } else {
        showNotification("Unable to generate quotation number", "error");
      }
    } catch {
      if (request === quotationRequest.current) showNotification("Unable to generate quotation number", "error");
    } finally {
      if (request === quotationRequest.current) setGeneratingQuotation(false);
    }
  };

  const saveSales = (values: any) => {
    if (generatingQuotation) return;
    const toOptionalNumber = (value: any) => {
      if (value === undefined || value === null || value === "") return undefined;
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : undefined;
    };
    const payload = {
      ...values,
      receivedDate: values.receivedDate?.format("YYYY-MM-DD"),
      estimatedClosureDate: values.estimatedClosureDate?.format("YYYY-MM-DD"),
      dealValue: Number(values.dealValue),
      grossMargin: toOptionalNumber(values.grossMargin),
      probability: toOptionalNumber(values.probability),
    };
    const action = selectedEntity
      ? updateSalesById({ salesId: selectedEntity.salesId, body: payload })
      : createSales(payload);
    dispatch(action).then((result: any) => {
      if (result?.payload?.data?.statusCode === 200) {
        showNotification("Sales record saved successfully", "success");
        setDrawerOpen(false);
        loadList();
      } else {
        showNotification("Unable to save sales record", "error");
      }
    });
  };

  const convertedValueText = () => {
    if (!converted) return "";
    const value = Number(converted.value);
    const rate = Number(converted.rate);
    if (!Number.isFinite(value) || !Number.isFinite(rate)) return "";
    return value.toFixed(2);
  };

  const convertedRate = converted ? Number(converted.rate) : NaN;
  const targetValueLabel = Number.isFinite(convertedRate)
    ? `Deal Value (Reporting) @ ${convertedRate}`
    : "Deal Value (Reporting)";

  const exportSales = () => {
    const rows = salesList.map((item: any) => ({
      "Deal Ref": item.dealReference,
      Entity: item.entityName,
      Client: item.clientName,
      Opportunity: item.opportunity,
      Stage: item.dealStageName,
      "Sub Stage": item.dealSubStageName,
      "Deal Value": item.dealValue,
      "Deal Currency": item.dealCurrencyName,
      "Deal Value (Reporting)": item.dealValueTarget,
      "Reporting Currency": item.targetCurrencyName,
      "Probability (%)": item.probability,
      Priority: item.priority,
    }));
    const csv = Papa.unparse(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "sales.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const allColumns = useMemo(
    () => [
      { title: "Deal Ref", dataIndex: "dealReference", key: "dealReference" },
      { title: "Entity", dataIndex: "entityName", key: "entityName" },
      { title: "Sale Received Date", dataIndex: "receivedDate", key: "receivedDate", render: (v: string) => v ? dayjs(v).format("DD-MMM-YYYY") : "" },
      { title: "Client", dataIndex: "clientName", key: "clientName" },
      { title: "Location", dataIndex: "clientLocation", key: "clientLocation" },
      { title: "Opportunity Type", dataIndex: "projectTypeName", key: "projectTypeName" },
      { title: "Opportunity", dataIndex: "opportunity", key: "opportunity" },
      { title: "Lead Owner", dataIndex: "leadOwnerName", key: "leadOwnerName" },
      { title: "Delivery Owner", dataIndex: "deliveryOwnerName", key: "deliveryOwnerName" },
      { title: "Est. Closure", dataIndex: "estimatedClosureDate", key: "estimatedClosureDate", render: (v: string) => v ? dayjs(v).format("DD-MMM-YYYY") : "" },
      { title: "Closure Qtr", dataIndex: "closureQuarter", key: "closureQuarter", render: formatClosureQuarter },
      { title: "Stage", dataIndex: "dealStageName", key: "dealStageName" },
      { title: "Sub-Stage", dataIndex: "dealSubStageName", key: "dealSubStageName" },
      { title: "Deal Value", dataIndex: "dealValue", key: "dealValue", render: (v: number, r: any) => formatCost1(v, r.dealCurrencyName) },
      { title: "Deal Value (Reporting)", dataIndex: "dealValueTarget", key: "dealValueTarget", render: (v: number, r: any) => formatCost1(v, r.targetCurrencyName) },
      { title: "Gross Margin (%)", dataIndex: "grossMargin", key: "grossMargin", render: (v: any) => v === null || v === undefined || v === "" ? "" : `${v}%` },
      { title: "Probability (%)", dataIndex: "probability", key: "probability", render: (v: any) => v === null || v === undefined || v === "" ? "" : `${v}%` },
      { title: "SOW #", dataIndex: "quotationSowNumber", key: "quotationSowNumber" },
      { title: "Priority", dataIndex: "priority", key: "priority" },
      { title: "Remarks", dataIndex: "remarks", key: "remarks" },
      {
        title: "Actions",
        key: "actions",
        fixed: "right",
        width: 112,
        align: "center",
        render: (_: any, record: any) =>
          accessAllowed(myPermissions, allPermissions.UPDATE_SALES) ||
          accessAllowed(myPermissions, allPermissions.DELETE_SALES) ? (
            <Space>
              {accessAllowed(myPermissions, allPermissions.UPDATE_SALES) && (
                <Button
                  type="link"
                  icon={<EditOutlined />}
                  onClick={() => openEdit(record)}
                  title="Edit"
                  aria-label="Edit sales record"
                />
              )}
              {accessAllowed(myPermissions, allPermissions.DELETE_SALES) && (
                <Button
                  type="link"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => deleteSales(record)}
                  title="Delete"
                  aria-label="Delete sales record"
                />
              )}
            </Space>
          ) : null,
      },
    ],
    [myPermissions, salesList]
  );

  const columns = allColumns.filter(
    (column: any) => column.key === "actions" || visibleColumns.includes(column.key)
  );

  return (
    <div className="app-page" ref={pageRef}>
      {bulkOpen && <SalesBulkUpload onClose={() => setBulkOpen(false)} onImported={() => loadList(1)} />}
      <Modal title="Customize Table View" open={columnSettingsOpen} onCancel={() => setColumnSettingsOpen(false)}
        width={560} footer={<Space>
          <Button onClick={() => setVisibleColumns(DEFAULT_COLUMNS)}>Reset to Default</Button>
          <Button type="primary" onClick={() => setColumnSettingsOpen(false)}>Done</Button>
        </Space>}>
        <div style={{ maxHeight: "60vh", overflowY: "auto", overflowX: "hidden" }}>
          <Row gutter={[24, 0]} style={{ margin: 0 }}>
            {allColumns.filter((column) => column.key !== "actions").map((column) => {
              const checked = visibleColumns.includes(column.key);
              return <Col xs={24} sm={12} key={column.key}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "12px 0", borderBottom: "1px solid #e2e8f0" }}>
                  <span>{column.title}</span>
                  <Switch size="small" checked={checked} aria-label={String(column.title)} disabled={checked && visibleColumns.length === 1}
                    onChange={(enabled) => setVisibleColumns((previous) => enabled
                      ? [...previous, column.key]
                      : previous.filter((key) => key !== column.key))} />
                </div>
              </Col>;
            })}
          </Row>
        </div>
      </Modal>
      <div className="app-page-header">
        <Row align="middle" justify="space-between" wrap>
          <Col>
            <div className="app-page-title-wrap">
              <div className="app-page-icon">
                <SettingOutlined />
              </div>
              <h1 className="app-page-title">Manage Sales</h1>
            </div>
            <p className="app-page-subtitle">View and manage sales pipeline records</p>
          </Col>
          <Col>
            <Space className="app-page-actions">
              <Tooltip title="Customize Table View">
                <Button icon={<TableOutlined />} aria-label="Customize Table View" onClick={() => setColumnSettingsOpen(true)} />
              </Tooltip>
              <Button icon={<UploadOutlined />} disabled={!accessAllowed(myPermissions, allPermissions.SALES_BULK_CREATE)} onClick={() => setBulkOpen(true)}>
                Bulk Upload
              </Button>
              <Button icon={<DownloadOutlined />} onClick={exportSales}>
                Export
              </Button>
              {accessAllowed(myPermissions, allPermissions.CREATE_SALES) && (
                <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                  Add Sales
                </Button>
              )}
              <Button
                shape="circle"
                type="primary"
                icon={<BiFilterAlt />}
                onClick={() => setFilterVisible(!filterVisible)}
              />
            </Space>
          </Col>
        </Row>
      </div>

      {filterVisible && (
        <div className="app-filter-bar">
          <div className="app-filter-header">
            <div className="app-filter-title">
              <BiFilterAlt style={{ fontSize: 14 }} />
              Filters
            </div>
            <Button type="text" size="small" icon={<CloseOutlined />} onClick={() => setFilterVisible(false)} />
          </div>
          <Form form={filterForm} layout="vertical" onFinish={setFilters}>
            <Row gutter={12}>
              <Col xs={24} md={6}>
                <Form.Item name="entityId">
                  <Select mode="multiple" allowClear placeholder="Entity" options={entityList.map((e) => ({ value: e.id, label: e.name })).sort(compareOptionLabels)} />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item name="dealStageId">
                  <Select mode="multiple" allowClear placeholder="Deal Stage" options={stages.map((s) => ({ value: s.id, label: s.name })).sort(compareOptionLabels)} />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item name="ownerId">
                  <Select mode="multiple" allowClear optionFilterProp="label" placeholder="Owner" options={staffOptions} />
                </Form.Item>
              </Col>
              <Col xs={24} md={4}>
                <Form.Item name="priority">
                  <Select mode="multiple" allowClear placeholder="Priority" options={["1", "2", "3"].map((p) => ({ value: p, label: p }))} />
                </Form.Item>
              </Col>
              <Col xs={24} md={2}>
                <Button type="primary" htmlType="submit">Apply</Button>
              </Col>
            </Row>
          </Form>
        </div>
      )}

      <div className="app-table-card">
        <DataTableComponent
          rowKey="salesId"
          pagination
          loading={loadingSales}
          dataSource={salesList}
          columns={columns}
          currentPage={pagination?.page || 1}
          totalRows={pagination?.total || 0}
          defaultPage={0}
          onPageChange={(page: number) => loadList(page)}
          onPageSizeChange={(_: number, size: number) => setPageSize(size)}
          pageSize={pageSize}
          pageSizeOptions={["10", "15", "25", "50", "100"]}
          tableWidth="max-content"
        />
      </div>

      <Drawer
        maskClosable={false}
        title={selectedEntity ? "Edit Sales" : "Add Sales"}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        placement="bottom"
        height="50dvh"
        rootStyle={{ left: contentLeft }}
        className="form-drawer"
        bodyStyle={{ overflowY: "auto", overflowX: "hidden" }}
        footer={<Space>
          <Button onClick={() => setDrawerOpen(false)}>Cancel</Button>
          <Button type="primary" disabled={generatingQuotation} onClick={() => form.submit()}>Save</Button>
        </Space>}
      >
        <Form form={form} layout="vertical" onFinish={saveSales} onValuesChange={lookupConversion}>
          <Row gutter={20} style={{ margin: 0 }}>
            <Col xs={24} sm={12} lg={8}><Form.Item name="entityId" label="Entity" rules={[{ required: true }]}><Select options={entityList.map((e) => ({ value: e.id, label: e.name })).sort(compareOptionLabels)} /></Form.Item></Col>
            <Col xs={24} sm={12} lg={8}><Form.Item name="receivedDate" label="Sale Received Date" rules={[{ required: true }]}><DatePicker style={{ width: "100%" }} /></Form.Item></Col>
            <Col xs={24} sm={12} lg={8}><Form.Item name="clientId" label="Client" rules={[{ required: true }]}><Select showSearch options={accounts.map((a) => ({ value: a.accountId, label: a.accountName })).sort(compareOptionLabels)} /></Form.Item></Col>
            <Col xs={24} sm={12} lg={8}><Form.Item name="clientLocation" label="Client Location"><Select showSearch options={Country.getAllCountries().map((c) => ({ value: c.isoCode, label: c.name })).sort(compareOptionLabels)} /></Form.Item></Col>
            <Col xs={24} sm={12} lg={8}><Form.Item name="projectTypeId" label="Opportunity Type" rules={[{ required: true }]}><Select options={projectTypes.map((t) => ({ value: t.id, label: t.name })).sort(compareOptionLabels)} /></Form.Item></Col>
            <Col xs={24} sm={12} lg={8}><Form.Item name="opportunity" label="Opportunity" rules={[{ required: true, max: 100 }]}><Input maxLength={100} /></Form.Item></Col>
            <Col xs={24} sm={12} lg={8}><Form.Item name="leadOwnerId" label="Lead Owner" rules={[{ required: true }]}><Select showSearch optionFilterProp="label" options={staffOptions} /></Form.Item></Col>
            <Col xs={24} sm={12} lg={8}><Form.Item name="deliveryOwnerId" label="Delivery Owner" rules={[{ required: true }]}><Select showSearch optionFilterProp="label" options={staffOptions} /></Form.Item></Col>
            <Col xs={24} sm={12} lg={8}><Form.Item name="estimatedClosureDate" label="Est. Closure Date" rules={[{ required: true }]}><DatePicker style={{ width: "100%" }} disabledDate={(current) => !!(current && current < dayjs().startOf("day"))} /></Form.Item></Col>
            <Col xs={24} sm={12} lg={8}><Form.Item label="Closure Qtr"><Input disabled value={closureQuarter} /></Form.Item></Col>
            <Col xs={24} sm={12} lg={8}><Form.Item name="dealStageId" label="Deal Stage" rules={[{ required: true }]}><Select options={stages.map((s) => ({ value: s.id, label: s.name })).sort(compareOptionLabels)} onChange={(value) => { form.setFieldValue("dealSubStageId", undefined); loadSubStages(value); }} /></Form.Item></Col>
            <Col xs={24} sm={12} lg={8}><Form.Item name="dealSubStageId" label="Deal Sub-Stage"><Select allowClear disabled={!subStages.length} options={subStages.map((s) => ({ value: s.id, label: s.name })).sort(compareOptionLabels)} /></Form.Item></Col>
            <Col xs={24} sm={12} lg={8}><Form.Item name="dealCurrencyId" label="Deal Currency" rules={[{ required: true }]}><Select options={currencyOptions} /></Form.Item></Col>
            <Col xs={24} sm={12} lg={8}><Form.Item name="dealValue" label="Deal Value" rules={[{ required: true }]}><InputNumber min={0} precision={2} style={{ width: "100%" }} /></Form.Item></Col>
            <Col xs={24} sm={12} lg={8}><Form.Item name="targetCurrencyId" label="Reporting Currency" rules={[{ required: true }]}><Select options={currencyOptions} /></Form.Item></Col>
            <Col xs={24} sm={12} lg={8}><Form.Item label={targetValueLabel}><Input disabled value={convertedValueText()} /></Form.Item></Col>
            <Col xs={24} sm={12} lg={8}><Form.Item name="grossMargin" label="Gross Margin (%)"><InputNumber addonAfter="%" min={0} max={100} precision={2} style={{ width: "100%" }} /></Form.Item></Col>
            <Col xs={24} sm={12} lg={8}><Form.Item name="probability" label="Probability (%)"><InputNumber addonAfter="%" min={0} max={100} precision={2} style={{ width: "100%" }} /></Form.Item></Col>
            <Col xs={24} sm={12} lg={8}><Form.Item label="Quotation / SOW #">
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                <Form.Item name="quotationSowNumber" noStyle><Input aria-label="Quotation / SOW number" disabled={generatingQuotation} style={{ flex: "1 1 160px", minWidth: 0 }} /></Form.Item>
                <Tooltip title="Generate Quotation Number"><Button icon={<FileAddOutlined />} aria-label="Generate Quotation Number"
                  loading={generatingQuotation} onClick={() => {
                    if (form.getFieldValue("quotationSowNumber")) {
                      Modal.confirm({ title: "Replace quotation number?", content: "Generate a new number to replace the current value?", okText: "Generate", onOk: generateQuotationNumber });
                    } else { generateQuotationNumber(); }
                  }}>Generate</Button></Tooltip>
              </div>
            </Form.Item></Col>
            <Col xs={24} sm={12} lg={8}><Form.Item name="priority" label="Priority" rules={[{ required: true }]}><Select options={["1", "2", "3"].map((p) => ({ value: p, label: p }))} /></Form.Item></Col>
            <Col span={24}><Form.Item name="remarks" label="Remarks"><Input.TextArea maxLength={500} /></Form.Item></Col>
          </Row>
        </Form>
      </Drawer>
    </div>
  );
};

export default ManageSalesPage;
